import { Route } from 'vue-router';

import { EntityType } from '@/enums/entityType.enum';
import { ModuleName } from '@/enums/moduleName.enum';
import { Options } from '@/enums/options.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { StageType } from '@/enums/stageType.enum';
import { Storage } from '@/enums/storage.enum';
import { TableName } from '@/enums/tableName';
import { Filter } from '@/interfaces/filter';
import { OptionValue } from '@/interfaces/optionValue';
import { Schema } from '@/interfaces/schema';
import { documentDetailsRoute } from '@/router/mainRoute/procedureRoute/documentRoute/details.route';
import store from '@/store/store';
import { getColumnsSetting } from './columnSetting';
import { getFiltersSetting } from './filterSetting';
import { isNullable } from './isNullable';
import { tryParseInt } from './number';
import { prepareQuery } from './prepareQuery';
import { getProfileId } from './profile';

/**
 * Pobiera szczegóły dokumentu
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getDocumentDetails(route: Route): Promise<any> {
  store.commit('documentIndex/setId', tryParseInt(route.params.documentId));
  return Promise.all([
    store.dispatch('documentIndex/get').then(documentIndex => {
      const ids = [...(documentIndex.previousStatuses || []), documentIndex.statusId];
      return Promise.all([
        store.dispatch('document/status/list', { ids, module: ModuleName.assessment }),
        store
          .dispatch('setting/publicList', {
            codes: [
              PublicSettings.PROCEDURE_DOCUMENT_DETAILS_BREADCRUMB,
              PublicSettings.HIDE_DOCUMENT_DOCUMENTATION_FOR_JO
            ],
            entityType: TableName.procedure,
            entityId: route.params.procedureId.toString()
          })
          .then(setting => {
            if (setting) {
              documentDetailsRoute.meta.breadcrumb.title = setting.value;
            } else {
              documentDetailsRoute.meta.breadcrumb.title = documentIndex.data?.title || 'Szczegóły wniosku';
            }
          })
      ]);
    })
  ]);
}

/**
 * Pobiera dane dokumentu
 *
 * @returns {Promise<any>}
 * @export
 */
export function getDocumentViewData(route: Route): Promise<any> {
  const documentId = tryParseInt(route.params.documentId);
  const procedureId = route.params.procedureId.toString();
  store.commit('document/setId', documentId);
  store.commit('documentIndex/setId', documentId);
  return Promise.all([
    store
      .dispatch('document/get')
      .then(document => store.dispatch('button/list', { statusId: document.status.id, count: -1 })),
    store.dispatch('documentIndex/get').then(documentIndex =>
      store.dispatch('document/status/list', {
        ids: [...(documentIndex.previousStatuses || []), documentIndex.statusId],
        module: ModuleName.assessment
      })
    ),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.PROCEDURE_WITHOUT_INFO,
      entityType: EntityType.PROCEDURE,
      entityId: procedureId
    }),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.SUBMITTED_MANUALLY_TEXT,
      entityType: EntityType.PROCEDURE,
      entityId: procedureId
    }),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.ARCHIVE_STATUSES,
      entityType: EntityType.PROCEDURE,
      entityId: procedureId
    }),
    getDocumentViewSettings(route)
  ]);
}

/**
 * Pobiera dane widoku listy dokumentów
 *
 * @export
 * @param {Route} route
 * @returns {Promise<any>}
 */
export function getDocumentListData(route: Route): Promise<any> {
  return Promise.all([
    getDocumentsWithDefaultSort(route),
    getFiltersSetting(route),
    getDocumentViewSettings(route),
    getSchema(route),
    getButtons(route),
    store.dispatch('setting/findPublic', {
      code: PublicSettings.PROCEDURE_TABLE_BUTTONS,
      entityType: EntityType.PROCEDURE,
      entityId: route.params.procedureId.toString()
    }),
    store
      .dispatch('setting/findPublic', {
        code: PublicSettings.PROCEDURE_GET_PDF_BUTTON_TEXT,
        entityType: EntityType.PROCEDURE,
        entityId: route.params.procedureId.toString()
      })
      .then(
        setting =>
          setting || store.dispatch('setting/findPublic', { code: PublicSettings.PROCEDURE_GET_PDF_BUTTON_TEXT })
      )
  ]);
}

/**
 * Pobiera liste dokumentów z domyślnym sortowaniem z kolumn
 *
 * @export
 * @param {Route} route
 * @returns {Promise<void>}
 */
export function getDocumentsWithDefaultSort(route: Route): Promise<void> {
  return getColumnsSetting(route).then(() =>
    store.dispatch('documentIndex/list', documentIndexFilters(route, { withDefaultSort: true }))
  );
}

/**
 * Filtry indeksu dokumentów
 *
 * @export
 * @param {Route} route
 * @param {boolean} [withProfileId=false]
 * @returns {object}
 */
export function documentIndexFilters(
  route: Route,
  listOptions?: { withProfileId?: boolean; withDefaultSort?: boolean; disallowHidden?: boolean }
): Filter {
  const previousStatuses = tryParseInt(route.query.previousStatuses);
  const notStatus = tryParseInt(route.query.notPreviousStatus);
  const previousStatusesQuery: any = {};
  if (previousStatuses) {
    previousStatusesQuery.$or = [{ previousStatuses }, { statusId: previousStatuses }];
  }
  if (notStatus) {
    previousStatusesQuery.$and = [{ previousStatuses: { $ne: notStatus } }, { statusId: { $ne: notStatus } }];
  }
  return {
    ...prepareQuery(route, listOptions?.withDefaultSort),
    filterFields: {
      procedureId: tryParseInt(route.params.procedureId),
      statusId: tryParseInt(route.query.statusId),
      parentId: tryParseInt(route.query.parentId),
      'procedure.hidden': listOptions?.disallowHidden ? { $ne: true } : undefined,
      module: listOptions?.disallowHidden ? { $ne: ModuleName.hidden } : undefined,
      ...getNodeFilters(listOptions?.withProfileId),
      creatorId: listOptions?.withProfileId ? getProfileId() : undefined,
      $and: [
        {
          ...previousStatusesQuery
        },
        {
          ...getStorageFiltersForQuery(route)
        }
      ]
    }
  };
}

/**
 * Pobiera przyciski
 *
 * @param {Route} route
 * @returns
 */
function getButtons(route: Route) {
  const statusId = tryParseInt(route.query.statusId);
  if (statusId) {
    return Promise.all([
      store.dispatch('button/list', { statusId, count: -1 }),
      store.dispatch('status/get', statusId),
      store.dispatch('setting/findPublic', {
        code: PublicSettings.PACK_CREATE_SHOW,
        entityType: EntityType.STATUS,
        entityId: statusId.toString()
      })
    ]);
  }
  return Promise.all([
    store.commit('button/saveList', []),
    store.commit('setting/deleteByCode', PublicSettings.PACK_CREATE_SHOW)
  ]);
}

/**
 * Zwraca filtry węzłów dla sekretarki
 *
 * @param {boolean} withProfileId
 * @returns {object}
 */
function getNodeFilters(withProfileId?: boolean): object {
  if (withProfileId) {
    return {};
  }
  if (store.state.auth.nodes?.length) {
    return { 'data.school.nodeId': { $in: store.state.auth.nodes.map((node: any) => node.id) } };
  }
  return {};
}

/**
 * Zwraca gotowe do przesłania do API filtry
 *
 * @returns {object}
 */
function getStorageFiltersForQuery(route: Route): object {
  const storageFilters = getFilters(route.params.procedureId?.toString());
  flattenNestedQueries(storageFilters);
  return storageFilters;
}

/**
 * Spłaszcza strukturę zapytania
 *
 * @param {*} filtersObject
 */
export function flattenNestedQueries(filtersObject: any): void {
  Object.keys(filtersObject).forEach(key => {
    const filter = filtersObject[key];
    if (key.startsWith('_') || isNullable(filter)) {
      filtersObject[key] = undefined;
    }

    if (typeof filter === 'object') {
      flattenNestedQueries(filter);
      if (!Object.keys(filter).filter(filterKey => !isNullable(filter[filterKey])).length) {
        filtersObject[key] = undefined;
      }
    }

    if (key.endsWith('.') && filtersObject[key]) {
      Object.keys(filter).forEach(filterKey => (filtersObject[key + filterKey] = filter[filterKey]));
      filtersObject[key] = undefined;
    }
  });
}

/**
 * Odczytuje aktualne filtry
 *
 * @export
 * @returns {object}
 */
export function getFilters(entityId?: string): object {
  const storageKey = Storage.FILTERS + (entityId || '');
  const unparsed = localStorage.getItem(storageKey);
  if (unparsed) {
    return JSON.parse(unparsed);
  }
  return {};
}

/**
 * Pobiera ustawienie z widokiem dokumentu
 *
 * @param {Route} route
 * @returns {Promise<void>}
 */
function getDocumentViewSettings(route: Route): Promise<any> {
  return store.dispatch('view/listByStatusOption', { statusId: tryParseInt(route.query.statusId), multipleOnly: true });
}

/**
 * Pobiera schemę
 *
 * @param {Route} route
 * @returns {Promise<Schema>}
 */
function getSchema(route: Route): Promise<Schema> {
  return Promise.resolve(undefined)
    .then(() => {
      if (route.query.statusId) {
        return getStatusSchemaId(route.query.statusId.toString());
      }
    })
    .then(maybeSchemaValue => {
      if (
        route.query.statusId &&
        maybeSchemaValue &&
        maybeSchemaValue.entityType === TableName.status &&
        maybeSchemaValue.entityId === route.query.statusId.toString()
      ) {
        return maybeSchemaValue.value;
      }
      return store
        .dispatch('status/list', {
          count: 1,
          procedureId: tryParseInt(route.params.procedureId),
          first: true,
          type: StageType.DOCUMENT
        })
        .then(statuses => statuses.list[0].id)
        .then(statusId => getStatusSchemaId(statusId.toString()))
        .then(schemaValue => schemaValue.value);
    })
    .then(schemaId => store.dispatch('schema/get', tryParseInt(schemaId)));
}

/**
 * Pobiera identyfikator schemy statusu
 *
 * @param {string} statusId
 * @returns {Promise<number>}
 */
function getStatusSchemaId(statusId: string): Promise<OptionValue> {
  return store
    .dispatch('optionValue/getByCode', {
      code: Options.SCHEMA_ID,
      entityType: TableName.status,
      entityId: statusId
    })
    .catch(() => undefined);
}
